import { useSearchParams } from "#routers/hooks";
import { ApplyAutomationProgressBanner } from "#src/batteries-included-components/Banners/ApplyAutomationProgressBanner";
import { ImportDataActionStatusWithQueue } from "#src/batteries-included-components/Banners/ImportDataActionStatus/ImportDataActionStatus";
import { getImportDataActionQueueKey } from "#src/batteries-included-components/Buttons/ImportDataAction/ImportDataAction.helpers";
import {
  EquipmentRecordsTableFilterArea,
  EquipmentRecordsViewFilterArea,
  FacilityRecordsTableFilterArea,
  FacilityRecordsViewFilterArea,
  FlowRecordsTableFilterArea,
  FlowRecordsViewFilterArea,
} from "#src/batteries-included-components/FilterAreas/RecordsFilterAreas";
import { RecordsListTablePanel } from "#src/batteries-included-components/index";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { RECORDS_PAGE_BREADCRUMB } from "#src/routes/records/index";
import {
  RECORDS_BREADCRUMB,
  RECORDS_TITLE,
} from "#src/routes/records/records/index";
import {
  generateDocumentTitle,
  Page,
  Tab,
} from "@validereinc/common-components";
import { AssetType, ResourceDefinitions, Resources } from "@validereinc/domain";
import React from "react";
import { Titled } from "react-titled";

export const RecordsListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { localize } = useLocalization();
  const [calcsAndRecordsSharedViewConfigStorageKey] = useStorageKey(
    ["view-configuration"],
    "calculations-records-shared"
  );
  const [
    equipmentFilterConfigStorageKey,
    equipmentTableConfigStorageKey,
    equipmentTableStateStorageKey,
  ] = useStorageKey(
    ["filter-configuration", "table-configuration", "table-state"],
    ["records", "list", "equipment"]
  );
  const [equipmentRecordsApplyAutomationStorageKey] = useStorageKey(
    ["async-job"],
    ["records", "list", "equipment", "apply-automation"]
  );
  const [equipmentConfigsApplyAutomationStorageKey] = useStorageKey(
    ["async-job"],
    "equipment-configs-apply-automation"
  );
  const [
    flowsFilterConfigStorageKey,
    flowsTableConfigStorageKey,
    flowsTableStateStorageKey,
  ] = useStorageKey(
    ["filter-configuration", "table-configuration", "table-state"],
    ["records", "list", "flows"]
  );
  const [flowRecordsApplyAutomationStorageKey] = useStorageKey(
    ["async-job"],
    ["records", "list", "flows", "apply-automation"]
  );
  const [flowConfigsApplyAutomationStorageKey] = useStorageKey(
    ["async-job"],
    "flows-configs-apply-automation"
  );
  const [
    facilitiesFilterConfigStorageKey,
    facilitiesTableConfigStorageKey,
    facilitiesTableStateStorageKey,
  ] = useStorageKey(
    ["filter-configuration", "table-configuration", "table-state"],
    ["records", "list", "facilities"]
  );
  const [facilityRecordsApplyAutomationStorageKey] = useStorageKey(
    ["async-job"],
    ["records", "list", "facilities", "apply-automation"]
  );
  const [facilityConfigsApplyAutomationStorageKey] = useStorageKey(
    ["async-job"],
    "facilities-configs-apply-automation"
  );
  const [isFlowEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:flows",
    permissionQuery: "flows:read",
  });
  const [isEquipmentEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:equipment",
    permissionQuery: "equipment:read",
  });
  const [isFacilityEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:facilities",
    permissionQuery: "facilities:read",
  });

  const DEFAULT_TAB_KEY = isEquipmentEnabled
    ? "equipment"
    : isFlowEnabled
      ? "flows"
      : "facilities";
  const equipmentRecordsImportDataQueueKey = getImportDataActionQueueKey({
    resource: {
      ...ResourceDefinitions.record,
      label: {
        singular: `${localize(ResourceDefinitions.equipment.label.singular)} Record`,
        plural: `${localize(ResourceDefinitions.equipment.label.singular)} Records`,
      },
    },
  });
  const flowRecordsImportDataQueueKey = getImportDataActionQueueKey({
    resource: {
      ...ResourceDefinitions.record,
      label: {
        singular: `${localize(ResourceDefinitions.flow.label.singular)} Record`,
        plural: `${localize(ResourceDefinitions.flow.label.singular)} Records`,
      },
    },
  });
  const facilityRecordsImportDataQueueKey = getImportDataActionQueueKey({
    resource: {
      ...ResourceDefinitions.record,
      label: {
        singular: `${localize(ResourceDefinitions.facility.label.singular)} Record`,
        plural: `${localize(ResourceDefinitions.facility.label.singular)} Records`,
      },
    },
  });

  return (
    <Page
      title={RECORDS_TITLE}
      breadcrumbs={[RECORDS_PAGE_BREADCRUMB, RECORDS_BREADCRUMB]}
      activeTabKey={searchParams?.tab ?? DEFAULT_TAB_KEY}
      onActiveTabKeyChange={(tabKey: string) => {
        setSearchParams({ tab: tabKey });
      }}
    >
      {isEquipmentEnabled ? (
        <Tab
          title={localize("equipment_plural")}
          tabKey="equipment"
        >
          <Titled
            title={generateDocumentTitle(
              `${localize(Resources.EQUIPMENT)} ${ResourceDefinitions.record.label.plural}`
            )}
          >
            <ImportDataActionStatusWithQueue
              queueKey={equipmentRecordsImportDataQueueKey}
            />
            <ApplyAutomationProgressBanner
              jobStorageKey={equipmentRecordsApplyAutomationStorageKey}
              showCounts={true}
            />
            <ApplyAutomationProgressBanner
              jobStorageKey={equipmentConfigsApplyAutomationStorageKey}
              showCounts={false}
            />
            <EquipmentRecordsViewFilterArea
              viewConfigStorageKey={calcsAndRecordsSharedViewConfigStorageKey}
            />
            <RecordsListTablePanel
              type={AssetType.EQUIPMENT}
              filterConfigStorageKey={equipmentFilterConfigStorageKey}
              tableConfigStorageKey={equipmentTableConfigStorageKey}
              tableStateStorageKey={equipmentTableStateStorageKey}
              viewConfigStorageKey={calcsAndRecordsSharedViewConfigStorageKey}
              applyAutomationJobStorageKey={
                equipmentRecordsApplyAutomationStorageKey
              }
              applySeededAutomationJobStorageKey={
                equipmentConfigsApplyAutomationStorageKey
              }
              filterComponentSlot={({ recordValueTypes }) => (
                <EquipmentRecordsTableFilterArea
                  filterConfigStorageKey={equipmentFilterConfigStorageKey}
                  selectableProperties={recordValueTypes}
                />
              )}
            />
          </Titled>
        </Tab>
      ) : null}
      {isFlowEnabled ? (
        <Tab
          title={localize("flow_plural")}
          tabKey="flows"
        >
          <Titled
            title={generateDocumentTitle(
              `${localize(Resources.FLOW)} ${ResourceDefinitions.record.label.plural}`
            )}
          >
            <ImportDataActionStatusWithQueue
              queueKey={flowRecordsImportDataQueueKey}
            />
            <ApplyAutomationProgressBanner
              jobStorageKey={flowRecordsApplyAutomationStorageKey}
              showCounts={true}
            />
            <ApplyAutomationProgressBanner
              jobStorageKey={flowConfigsApplyAutomationStorageKey}
              showCounts={false}
            />
            <FlowRecordsViewFilterArea
              viewConfigStorageKey={calcsAndRecordsSharedViewConfigStorageKey}
            />
            <RecordsListTablePanel
              type={AssetType.FLOW}
              filterConfigStorageKey={flowsFilterConfigStorageKey}
              tableConfigStorageKey={flowsTableConfigStorageKey}
              tableStateStorageKey={flowsTableStateStorageKey}
              viewConfigStorageKey={calcsAndRecordsSharedViewConfigStorageKey}
              applyAutomationJobStorageKey={
                flowRecordsApplyAutomationStorageKey
              }
              applySeededAutomationJobStorageKey={
                flowConfigsApplyAutomationStorageKey
              }
              filterComponentSlot={({ recordValueTypes }) => (
                <FlowRecordsTableFilterArea
                  filterConfigStorageKey={flowsFilterConfigStorageKey}
                  selectableProperties={recordValueTypes}
                />
              )}
            />
          </Titled>
        </Tab>
      ) : null}
      {isFacilityEnabled ? (
        <Tab
          title={`${localize("facility_plural")}`}
          tabKey="facilities"
        >
          <Titled
            title={generateDocumentTitle(
              `${localize(Resources.FACILITY)} ${ResourceDefinitions.record.label.plural}`
            )}
          >
            <ImportDataActionStatusWithQueue
              queueKey={facilityRecordsImportDataQueueKey}
            />
            <ApplyAutomationProgressBanner
              jobStorageKey={facilityRecordsApplyAutomationStorageKey}
              showCounts={true}
            />
            <ApplyAutomationProgressBanner
              jobStorageKey={facilityConfigsApplyAutomationStorageKey}
              showCounts={false}
            />
            <FacilityRecordsViewFilterArea
              viewConfigStorageKey={calcsAndRecordsSharedViewConfigStorageKey}
            />
            <RecordsListTablePanel
              type={AssetType.FACILITY}
              filterConfigStorageKey={facilitiesFilterConfigStorageKey}
              tableConfigStorageKey={facilitiesTableConfigStorageKey}
              tableStateStorageKey={facilitiesTableStateStorageKey}
              viewConfigStorageKey={calcsAndRecordsSharedViewConfigStorageKey}
              applyAutomationJobStorageKey={
                facilityRecordsApplyAutomationStorageKey
              }
              applySeededAutomationJobStorageKey={
                facilityConfigsApplyAutomationStorageKey
              }
              filterComponentSlot={({ recordValueTypes }) => (
                <FacilityRecordsTableFilterArea
                  filterConfigStorageKey={facilitiesFilterConfigStorageKey}
                  selectableProperties={recordValueTypes}
                />
              )}
            />
          </Titled>
        </Tab>
      ) : null}
    </Page>
  );
};
